// src/services/http.ts
// Centralized fetch helper: cookies, timeout, JSON parsing, error shaping.

export type HttpOptions = {
  method?: string;
  headers?: Record<string, string>;
  body?: any;
  timeout?: number;
  raw?: boolean; // if true: return Response for blob/arrayBuffer
};

export type HttpError = Error & {
  status?: number;
  body?: unknown;
  url?: string;
  method?: string;
};

const DEFAULT_TIMEOUT_MS = 15_000;

export async function http<T = any>(
  url: string,
  {
    method = 'GET',
    headers = {},
    body,
    timeout = DEFAULT_TIMEOUT_MS,
    raw = false,
  }: HttpOptions = {}
): Promise<T | Response> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort('timeout'), timeout);

  try {
    const h: Record<string, string> = { ...headers };

    // Only set JSON header if body is a plain object (not FormData/Blob/string)
    const isJsonBody =
      body != null &&
      !(body instanceof FormData) &&
      typeof body !== 'string' &&
      !(body instanceof Blob);

    const finalBody = isJsonBody ? JSON.stringify(body) : body;
    if (isJsonBody) h['Content-Type'] = h['Content-Type'] || 'application/json';

    const res = await fetch(url, {
      method,
      headers: h,
      body: finalBody,
      credentials: 'include', // keep session cookies
      signal: controller.signal,
    });

    if (raw) {
      if (!res.ok) throw newHttpError(res, null, url, method);
      return res;
    }

    // Try JSON; gracefully fall back to text
    const contentType = res.headers.get('content-type') || '';
    let data: any;
    if (contentType.includes('application/json')) {
      try {
        data = await res.json();
      } catch (e: any) {
        throw new Error(`Invalid JSON response from ${url}: ${e?.message || e}`);
      }
    } else {
      const text = await res.text().catch(() => '');
      data = text || null;
    }

    if (!res.ok) {
      throw newHttpError(res, data, url, method);
    }

    return data as T;
  } finally {
    clearTimeout(id);
  }
}

function newHttpError(res: Response, data: unknown, url: string, method: string): HttpError {
  const message =
    (data as any)?.error ||
    (data as any)?.message ||
    `${method} ${url} failed (${res.status})`;

  const err: HttpError = new Error(message);
  err.status = res.status;
  err.body = data;
  err.url = url;
  err.method = method;
  return err;
}
